import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { checkUpload, UPLOAD_ACCEPT, UploadError, uploadFile } from "./upload";
import { useQueryClient } from "@tanstack/react-query";
import { errorMessage } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import { Field, FormError } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function AddFileDialog({ org, project, open, onOpenChange }: { org: string; project: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setFileError(null);
    setTitle("");
    setTitleError(null);
    setDescription("");
    setProgress(null);
    setError(null);
  };

  const choose = (f: File | undefined) => {
    setError(null);
    if (!f) return;
    setFile(f);
    setFileError(checkUpload(f));
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file) return setFileError(t("files.fileRequired"));
    const problem = checkUpload(file);
    if (problem) return setFileError(problem);
    if (!title.trim()) return setTitleError(t("files.titleRequired"));
    setTitleError(null);
    setProgress(0);
    try {
      await uploadFile(org, project, file, { title: title.trim(), description: description.trim() || undefined }, setProgress);
      await qc.invalidateQueries({ queryKey: queryKeys.files(org, project) });
      toast.success(t("files.uploaded"));
      reset();
      onOpenChange(false);
    } catch (e) {
      setProgress(null);
      setError(e instanceof UploadError ? e.message : errorMessage(e, t("files.uploadFailed")));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("files.addFileTitle")}</DialogTitle>
          <DialogDescription>{t("files.addFileIntro")}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <FormError message={error} />
          <Field label={t("files.fileLabel")} hint={t("files.fileHint")} error={fileError}>
            {(p) => (
              <input
                {...p}
                type="file"
                accept={UPLOAD_ACCEPT}
                className="block w-full text-sm file:mr-4 file:min-h-11 file:cursor-pointer file:rounded-full file:border-2 file:border-secondary file:bg-surface file:px-5 file:font-semibold file:text-heading hover:file:bg-secondary-subtle"
                onChange={(e) => choose(e.target.files?.[0])}
              />
            )}
          </Field>
          <Field label={t("files.titleLabel")} error={titleError}>
            {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />}
          </Field>
          <Field label={t("files.descriptionLabel")}>
            {(p) => <Textarea {...p} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />}
          </Field>
          {progress !== null ? (
            <div className="flex flex-col gap-1" role="status">
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken" aria-hidden="true">
                <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-sm text-fg-muted">{t("files.uploading", { pct: progress })}</span>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={progress !== null}>
              {t("files.upload")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
